import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  AddOutlined as AddOutlinedIcon,
  AssignmentTurnedInOutlined as AssignmentTurnedInOutlinedIcon,
  BuildOutlined as BuildOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  DirectionsCarOutlined as DirectionsCarOutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  MoreVert as MoreVertIcon,
  RefreshOutlined as RefreshOutlinedIcon,
  Search as SearchOutlinedIcon,
} from "../../../../components/icons";
import { useEffect, useMemo, useState } from "react";
import Loader from "../../../../components/Loader";
import {
  assignVehicle,
  fetchAllVehicles,
  getFleetBranches,
  getVehicleHistory,
  removeVehicle,
} from "../../../../services/dashboard";
import { useToast } from "../../../../hooks/useToast";
import VerifiedPopup from "../../../../components/VerifiedPopup";
import { useVerifiedStore } from "../../../../store/useVerifiedStore";
import { useVehicleStore } from "../../../../store/useVehicleStore";
import AssignVehiclePopup from "../popups/AssignVehiclePopup";
import ConfirmDeletePopup from "../../../../components/ConfirmDeletePopup";
import { useAuthStore } from "../../../../store/useAuthStore";
import History from "../../claims/common/History";
import {
  OperationsEmptyState,
  OperationsPageShell,
  formatOperationsStatus,
  ifasColors,
} from "../../ifas/components/OperationsUI";
import {
  StandardCountBadge,
  StandardMetricCard,
  StandardPager,
  StatusDotChip,
  Tone,
  operationsStatusTone,
  standardGhostBtnSx,
  standardPrimaryBtnSx,
  standardTableHeadCellSx,
  standardTableRowSx,
} from "../../ifas/components/StandardUI";

// The full VehicleStatus enum (backend: com.iceclaim.enums.VehicleStatus) —
// used for the status filter options and to prove every value is representable.
const VEHICLE_STATUS_OPTIONS = [
  "AVAILABLE",
  "ALLOCATED",
  "DISPATCHED",
  "IN_SERVICE",
  "UNDER_MAINTENANCE",
  "OUT_OF_SERVICE",
  "REMOVED",
];

const formatMileage = (value?: number | null) => {
  if (value === null || value === undefined) return null;
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

const DashCell = () => (
  <Typography component="span" sx={{ color: ifasColors.muted, fontWeight: 700 }}>
    —
  </Typography>
);

const Vehicles = ({ title, recentFilter, onSelectVehicle, onAddVehicleClick }: any) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [vehicleItems, setVehicleItems] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const rowsPerPage = 10;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [popupLabel, setPopupLabel] = useState("");
  const [openViewPopup, setOpenViewPopup] = useState(false);
  const [vehicleStatus, setVehicleStatus] = useState("");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState<any>(null);
  const [branchFilter, setBranchFilter] = useState("");
  const [branchOptions, setBranchOptions] = useState<any[]>([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const { showInfoToast } = useToast();
  const { showVerified, setShowVerified } = useVerifiedStore();
  const { first_name, last_name, email, user_name } = useAuthStore();

  const [statusCounts, setStatusCounts] = useState({
    AVAILABLE: 0,
    ALLOCATED: 0,
    DISPATCHED: 0,
    UNDER_MAINTENANCE: 0,
    OUT_OF_SERVICE: 0,
  });

  const branchCaption = `Across ${branchOptions.length} branch${branchOptions.length === 1 ? "" : "es"}`;

  const metrics: Array<{ label: string; value: number; caption: string; tone: Tone; icon: React.ReactNode }> = [
    {
      label: "Total Vehicles",
      value: totalElements || 0,
      caption: branchCaption,
      tone: "grey",
      icon: <DirectionsCarOutlinedIcon />,
    },
    {
      label: "Available",
      value: statusCounts.AVAILABLE || 0,
      caption: "Ready to assign",
      tone: "green",
      icon: <CheckCircleOutlineIcon />,
    },
    {
      label: "Allocated",
      value: statusCounts.ALLOCATED || 0,
      caption: "On active cases",
      tone: "purple",
      icon: <AssignmentTurnedInOutlinedIcon />,
    },
    {
      label: "Dispatched",
      value: statusCounts.DISPATCHED || 0,
      caption: "Out on the road",
      tone: "orange",
      icon: <LocalShippingOutlinedIcon />,
    },
    {
      label: "Under Maintenance",
      value: statusCounts.UNDER_MAINTENANCE || 0,
      caption: "Service required",
      tone: "amber",
      icon: <BuildOutlinedIcon />,
    },
  ];

  const vehicleColumns = [
    "Vehicle ID",
    "Registration",
    "Type",
    "Model",
    "Manufacturer",
    "Branch",
    "Mileage",
    "Current Work",
    "Status",
    "Actions",
  ];

  const columns = vehicleColumns;

  const fetchAllVehicleList = async (page = 0) => {
    try {
      setIsLoading(true);

      const response = await fetchAllVehicles(page, rowsPerPage, undefined, branchFilter || undefined);

      if (response.status === 200) {
        setVehicleItems(response.data.vehicles);
        setStatusCounts(response.data.statusCounts);
        setTotalElements(response.data.totalCount);
        setLoadError(false);
      } else {
        setLoadError(true);
        showInfoToast(response.data.message);
      }
    } catch (error) {
      console.error("Error fetching vehicles", error);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Free-text search and status filter run over the currently loaded page —
  // fetchAllVehicles has no search/status parameter, and F.1 AC3 requires no
  // new fetch signature. Branch filtering stays server-side via the existing hook.
  const visibleItems = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    return vehicleItems.filter((item: any) => {
      if (statusFilter && String(item?.status || "").toUpperCase() !== statusFilter) {
        return false;
      }

      if (!query) return true;

      const haystack = [item.vehicleNumber, item.vehicleRegistrationNumber, item.modelName, item.manufacturerName]
        .map((v: any) => String(v || "").toLowerCase())
        .join(" ");

      return haystack.includes(query);
    });
  }, [vehicleItems, searchText, statusFilter]);

  const handleAssignVehicle = (item: any) => {
    if (!item?.vehicleID) {
      showInfoToast("Unable to identify the selected vehicle");
      return;
    }

    setSelectedItem(item);
    setOpenViewPopup(true);
  };

  const handleAssign = async ({
    driverID,
    caseReference,
  }: {
    driverID: string;
    caseReference: string;
  }) => {
    if (!selectedItem?.vehicleID) {
      showInfoToast("Unable to identify the selected vehicle");
      return;
    }

    if (!driverID) {
      showInfoToast("Please select a driver");
      return;
    }

    if (!caseReference.trim()) {
      showInfoToast("Please enter case reference");
      return;
    }

    try {
      setIsLoading(true);
      const response = await assignVehicle(selectedItem.vehicleID, {
        driverID,
        caseReference: caseReference,
        assignedBy: `${first_name || ""} ${last_name || ""}`.trim()
          || user_name
          || email
          || "Fleet Coordinator",
      });

      if (response.status === 200 || response.status === 201) {
        setPopupLabel("Assigned");
        setShowVerified(true);
        setOpenViewPopup(false);
        setAnchorEl(null);
        setSelectedItem(null);
        await fetchAllVehicleList(currentPage);
      } else {
        showInfoToast(response?.data?.message || "Failed to assign vehicle");
      }
    } catch (error) {
      console.log(error);
      showInfoToast("Failed to assign vehicle");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllVehicleList(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, branchFilter]);

  useEffect(() => {
    const loadBranches = async () => {
      const response = await getFleetBranches();
      if (response?.status === 200 && Array.isArray(response.data)) {
        setBranchOptions(
          response.data
            .sort((a: any, b: any) =>
              String(a.branchName || "").localeCompare(String(b.branchName || "")),
            ),
        );
      }
    };

    void loadBranches();
  }, []);

  useEffect(() => {
    if (showVerified) {
      const timer = setTimeout(() => {
        setShowVerified(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [showVerified]);

  const handleRemoveVehicle = () => {
    if (!selectedItem?.vehicleID) {
      showInfoToast("Unable to identify the selected vehicle");
      return;
    }

    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (!selectedItem?.vehicleID) {
      showInfoToast("Unable to identify the selected vehicle");
      setShowConfirmDelete(false);
      return;
    }

    setIsLoading(true);
    try {
      const data = await removeVehicle(selectedItem.vehicleID, {
        status: "AVAILABLE",
        reason: "Vehicle removed from current assignment",
      });

      if (data.status === 200 || data.status === 201) {
        setPopupLabel("Released");
        setShowVerified(true);
        setShowConfirmDelete(false);
        setAnchorEl(null);
        setSelectedItem(null);
        await fetchAllVehicleList(currentPage);
      } else {
        setShowConfirmDelete(false);
        showInfoToast(data.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseConfirmPopup = () => {
    setShowConfirmDelete(false);
    setSelectedItem(null);
  };

  const handleHistoryClick = async () => {
    if (!selectedItem?.vehicleID) {
      showInfoToast("Unable to identify the selected vehicle");
      return;
    }

    setAnchorEl(null);
    setIsLoading(true);
    const res = await getVehicleHistory(selectedItem.vehicleID);
    if (res?.status === 200) {
      useVehicleStore.getState().setSelectedVehicle(selectedItem);
      setHistoryData(res.data);
      setShowHistory(true);
    } else {
      showInfoToast("Failed to load vehicle history. Please try again.");
    }
    setIsLoading(false);
  };

  const handleBackFromHistory = () => {
    setShowHistory(false);
  };

  const handleOpenDetails = (item: any) => {
    useVehicleStore.getState().setSelectedVehicle(item);
    onSelectVehicle(item.vehicleID);
  };

  if (showHistory) {
    return (
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <History
          onBack={handleBackFromHistory}
          historyData={historyData}
          isLoading={isLoading}
          historyType="case"
        />
      </Box>
    );
  }

  const branchFilterLabel = branchFilter
    ? branchOptions.find((branch: any) => branch.id === branchFilter)?.branchName || "Selected"
    : "All";
  const statusFilterLabel = statusFilter ? formatOperationsStatus(statusFilter) : "All";

  return (
    <OperationsPageShell
      eyebrow="Fleet Management"
      title={title}
      description="The fleet vehicle register — availability, assignment and service status across all branches. Open a vehicle for full details and mileage control."
      actions={
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => fetchAllVehicleList(currentPage)}
            sx={standardGhostBtnSx}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => onAddVehicleClick?.()}
            sx={standardPrimaryBtnSx}
          >
            Add Vehicle
          </Button>
        </Stack>
      }
    >
      {isLoading && <Loader isLoading={isLoading} />}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            lg: "repeat(5, minmax(0, 1fr))",
          },
          gap: 1.75,
          mb: 2.5,
        }}
      >
        {metrics.map((metric) => (
          <StandardMetricCard
            key={metric.label}
            label={metric.label}
            value={metric.value}
            caption={metric.caption}
            tone={metric.tone}
            icon={metric.icon}
            compact
          />
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2.25,
          mb: 2,
          border: `1px solid ${ifasColors.border}`,
          borderRadius: "16px",
          background: `linear-gradient(135deg, ${ifasColors.softOrange}, ${ifasColors.surface} 46%)`,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                bgcolor: ifasColors.softOrange,
                color: ifasColors.orange,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <DirectionsCarOutlinedIcon />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 19, fontWeight: 900, color: ifasColors.text }}>
                Vehicle Register
              </Typography>
              <Typography sx={{ mt: 0.4, color: ifasColors.muted, fontSize: 12.5, lineHeight: 1.45, maxWidth: 520 }}>
                Search and filter the fleet. Actions stay on each row.
              </Typography>
            </Box>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField
              size="small"
              placeholder="Search reg, ID, model…"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              InputProps={{ startAdornment: <SearchOutlinedIcon sx={{ mr: 1, color: ifasColors.muted }} /> }}
              sx={{ minWidth: 220, bgcolor: ifasColors.surface, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
            <TextField
              select
              size="small"
              value={branchFilter}
              onChange={(event) => {
                setCurrentPage(0);
                setBranchFilter(event.target.value);
              }}
              SelectProps={{ displayEmpty: true }}
              sx={{ minWidth: 190, bgcolor: ifasColors.surface, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            >
              <MenuItem value="">All branches</MenuItem>
              {branchOptions.map((branch: any) => (
                <MenuItem
                  key={branch.id}
                  value={branch.id}
                >
                  {branch.branchName}
                  {branch.province ? ` - ${branch.province}` : ""}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              SelectProps={{ displayEmpty: true }}
              sx={{ minWidth: 190, bgcolor: ifasColors.surface, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {VEHICLE_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {formatOperationsStatus(option)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${ifasColors.border}`,
          borderRadius: "16px",
          overflow: "hidden",
          bgcolor: ifasColors.surface,
          boxShadow: "0 4px 14px rgba(20,24,31,.05)",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2.25,
            borderBottom: `1px solid ${ifasColors.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.25,
            flexWrap: "wrap",
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: ifasColors.text }}>
              {recentFilter || "Vehicles"}
            </Typography>
            <StandardCountBadge count={totalElements || 0} />
          </Stack>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: ifasColors.muted }}>
            Branch: {branchFilterLabel} · Status: {statusFilterLabel}
          </Typography>
        </Box>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 1120 }}>
            <TableHead>
              <TableRow>
                {columns.map((col, index) => (
                  <TableCell
                    key={index}
                    align={col === "Actions" ? "right" : "left"}
                    sx={standardTableHeadCellSx}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loadError &&
                visibleItems.map((item: any) => {
                  const workRef =
                    item.currentCaseNumber || item.currentBodyRemovalNumber || item.currentAllocationNumber;
                  const workStatus = item.currentBodyRemovalStatus || item.currentAllocationStatus;
                  const mileage = formatMileage(item.currentMileage);

                  return (
                    <TableRow
                      key={item.id || item.vehicleID}
                      hover
                      sx={standardTableRowSx}
                      onClick={() => handleOpenDetails(item)}
                      style={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Typography fontWeight={900} color={ifasColors.text}>
                          {item.vehicleNumber}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>
                        {item.vehicleRegistrationNumber}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>{item.vehicleType}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>{item.modelName}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>{item.manufacturerName}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>
                        {item.branchName || item.branchLocation || "Unassigned"}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: ifasColors.text }}>
                        {mileage ? `${mileage} km` : "N/A"}
                      </TableCell>
                      <TableCell>
                        {workRef ? (
                          <Box display="flex" flexDirection="column" gap={0.25}>
                            <Typography fontWeight={900} color={ifasColors.text}>
                              {workRef}
                            </Typography>
                            {workStatus && (
                              <Typography fontSize={11.5} fontWeight={600} color={ifasColors.muted} mt={0.3}>
                                {formatOperationsStatus(workStatus)}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <DashCell />
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusDotChip status={item.status} tone={operationsStatusTone(item.status)} />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedItem(item);
                            setAnchorEl(e.currentTarget);
                            setVehicleStatus(item?.status);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {!loadError && visibleItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <OperationsEmptyState
                      title="No vehicles found"
                      description="No vehicles match this branch/search. Clear filters or add a vehicle."
                    />
                  </TableCell>
                </TableRow>
              )}
              {loadError && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <OperationsEmptyState
                      title="Couldn't load vehicles"
                      description="Something went wrong fetching the register."
                    />
                    <Box display="flex" justifyContent="center" pb={2}>
                      <Button variant="outlined" sx={standardGhostBtnSx} onClick={() => fetchAllVehicleList(currentPage)}>
                        Retry
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => {
              setAnchorEl(null);
              setSelectedItem(null);
            }}
          >
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                if (selectedItem) {
                  handleOpenDetails(selectedItem);
                }
              }}
            >
              Open details
            </MenuItem>
            {selectedItem?.status === "AVAILABLE" && (
              <MenuItem
                sx={{ color: ifasColors.deepOrange, fontWeight: 800 }}
                onClick={() => {
                  setAnchorEl(null);
                  if (selectedItem) {
                    handleAssignVehicle(selectedItem);
                  }
                }}
              >
                Assign
              </MenuItem>
            )}
            {["ALLOCATED", "DISPATCHED"].includes(String(selectedItem?.status || "").toUpperCase()) && (
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  handleRemoveVehicle();
                }}
              >
                Release
              </MenuItem>
            )}
            <Divider sx={{ my: 0.5 }} />
            <MenuItem
              onClick={() => {
                void handleHistoryClick();
              }}
            >
              History
            </MenuItem>
          </Menu>
        </TableContainer>

        <StandardPager
          page={currentPage}
          total={totalElements || 0}
          pageSize={rowsPerPage}
          onPage={setCurrentPage}
        />
      </Paper>

      <VerifiedPopup
        label={popupLabel}
        open={showVerified}
        onClose={() => setShowVerified(false)}
      />
      <ConfirmDeletePopup
        open={showConfirmDelete}
        onClose={handleCloseConfirmPopup}
        confirmDelete={confirmDelete}
      />
      <AssignVehiclePopup
        open={openViewPopup}
        selectedVehicle={selectedItem}
        onClose={() => {
          setOpenViewPopup(false);
          setSelectedItem(null);
        }}
        handleAssign={handleAssign}
      />
    </OperationsPageShell>
  );
};

export default Vehicles;
